import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import SearchOrigin from '@alltrails/amplitude/enums/SearchOrigin';
import logNaturalLanguageSuggestedPromptsDisplayed from '@alltrails/amplitude/events/logNaturalLanguageSuggestedPromptsDisplayed';
import ShimmerContainer from '@alltrails/core/components/ShimmerContainer';
import SearchSparkle from '@alltrails/denali/icons/SearchSparkle';
import type Context from '@alltrails/types/Context';
import { getNlSuggestions } from '../../requests/nlSearchRequests';
import styles from './styles/NLSearchSuggestions.module.scss';

const SuggestionShimmer = () => {
  return (
    <ShimmerContainer className={styles.suggestionsContainer}>
      <div className={styles.suggestionsRow}>
        <div className={classNames(styles.searchSuggestion, styles.loading)} />
        <div className={classNames(styles.searchSuggestion, styles.loading)} />
        <div className={classNames(styles.searchSuggestion, styles.loading)} />
      </div>
      <div className={styles.suggestionsRow}>
        <div className={classNames(styles.searchSuggestion, styles.loading)} />
        <div className={classNames(styles.searchSuggestion, styles.loading)} />
        <div className={classNames(styles.searchSuggestion, styles.loading)} />
      </div>
    </ShimmerContainer>
  );
};

type Props = {
  onSelect: (text: string, index: number) => void;
  searchOrigin?: SearchOrigin;
};

const NLSearchSuggestions = ({ onSelect, searchOrigin }: Props) => {
  const [isLoading, setIsLoading] = useState(true);
  const [suggestions, setSuggestions] = useState<{ text: string }[]>([]);
  const userLocation = useSelector((state: { context: Context }) => state.context?.userLocation);
  const userLat = userLocation?.lat;
  const userLng = userLocation?.lng;

  useEffect(() => {
    async function fetchSuggestions() {
      try {
        const center = userLat !== undefined && userLng !== undefined ? { lat: userLat, lng: userLng } : undefined;
        const response = await getNlSuggestions({ center });
        setSuggestions(response.suggestions);
        setIsLoading(false);
        if (searchOrigin) {
          logNaturalLanguageSuggestedPromptsDisplayed({
            num_prompts: response.suggestions.length,
            location: searchOrigin
          });
        }
      } catch (error) {
        console.error('Error fetching suggestions:', error);
      } finally {
        setIsLoading(false);
      }
    }

    fetchSuggestions();
  }, [searchOrigin, userLat, userLng]);

  if (isLoading) {
    return <SuggestionShimmer />;
  }

  const suggestionButtons = suggestions.map((suggestion, index) => (
    <button key={suggestion.text} className={styles.searchSuggestion} onClick={() => onSelect(suggestion.text, index)}>
      <SearchSparkle color="currentColor" size="sm" />
      {suggestion.text}
    </button>
  ));
  const totalCount = suggestions.length;
  const firstRowCount = Math.ceil(totalCount / 2);

  return (
    <div className={styles.suggestionsContainer}>
      <div className={styles.suggestionsRow}>{suggestionButtons.slice(0, firstRowCount).map(button => button)}</div>
      <div className={styles.suggestionsRow}>{suggestionButtons.slice(firstRowCount).map(button => button)}</div>
    </div>
  );
};

export default NLSearchSuggestions;
